import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import {
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Crown,
  Home,
  Lock,
  Megaphone,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';

import Modal from 'Containers/Common/Modal';
import { modalActions } from 'Containers/Modals/store';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import useWindowSize from 'utils/useWindowSize';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import styles from './WhatsNewModal.module.scss';

const SLIDE_ICONS = {
  home: Home,
  calendar: CalendarDays,
  stats: BarChart3,
  ads: Megaphone,
  expenses: Wallet,
  clients: Users,
  more: Sparkles,
};

const WhatsNewModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();
  const isPremiumUnlocked = useIsPremiumUnlocked();

  const slides = useMemo(() => {
    const raw = t('home.staticBlock.whatsNew.slides', { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
  }, [t]);

  const [swiper, setSwiper] = useState(null);
  const [index, setIndex] = useState(0);
  const total = slides.length;
  const isLast = index >= total - 1;

  useEffect(() => {
    if (!isOpen) {
      setIndex(0);
      setSwiper(null);
    }
  }, [isOpen]);

  const goToUpgrade = useCallback(() => {
    onClose();
    navigate(PREMIUM_UPGRADE_PATH);
  }, [dispatch, isMobile, navigate, onClose]);

  const next = () => {
    if (isLast) {
      onClose();
      return;
    }
    swiper?.slideNext();
  };
  const prev = () => swiper?.slidePrev();

  return (
    <Modal
      isModalOpen={isOpen}
      setIsModalOpen={(open) => {
        if (!open) onClose();
      }}
      centered
      showCloseBtn
      title={t('home.staticBlock.whatsNew.modalTitle')}
      subTitle=""
      width="560px"
      maxWidth="calc(100vw - 24px)"
      maxHeight="min(90vh, 90dvh)"
    >
      <div className={styles.content}>
        <div className={styles.progress} aria-hidden>
          {slides.map((slide, i) => (
            <span
              key={slide.key || i}
              className={clsx(styles.progressBar, {
                [styles.progressBarDone]: i < index,
                [styles.progressBarActive]: i === index,
              })}
            />
          ))}
        </div>

        <Swiper
          slidesPerView={1}
          spaceBetween={16}
          autoHeight={false}
          onSwiper={setSwiper}
          onSlideChange={(instance) => setIndex(instance.activeIndex)}
          className={styles.swiper}
        >
          {slides.map((slide, i) => {
            const Icon = SLIDE_ICONS[slide.key] || Sparkles;
            const slidePremium = !!slide.premium;
            const lockedSlide = slidePremium && !isPremiumUnlocked;
            const hasPremiumItems = (slide.items || []).some((item) => item?.premium);
            const showPremiumNote = !isPremiumUnlocked && (hasPremiumItems || slidePremium);

            return (
              <SwiperSlide key={slide.key || i} className={styles.slide}>
                <div className={styles.slideInner}>
                  <header
                    className={clsx(styles.slideHeader, {
                      [styles.slideHeaderPremium]: lockedSlide,
                    })}
                  >
                    <span className={styles.slideIcon} aria-hidden>
                      <Icon size={22} strokeWidth={2.2} />
                    </span>
                    <div className={styles.slideHeading}>
                      <span className={styles.slideStep}>
                        {t('home.staticBlock.whatsNew.stepOf', { current: i + 1, total })}
                        {lockedSlide && (
                          <span className={clsx(styles.premiumTag, styles.premiumTagLocked)}>
                            <Lock size={11} strokeWidth={2.6} aria-hidden />
                            {t('home.staticBlock.whatsNew.premiumFeature')}
                          </span>
                        )}
                      </span>
                      <h3 className={styles.slideTitle}>{slide.title}</h3>
                      {slide.subtitle && <p className={styles.slideSubtitle}>{slide.subtitle}</p>}
                    </div>
                  </header>

                  <ul className={styles.list}>
                    {(slide.items || []).map((item, j) => {
                      const text = typeof item === 'string' ? item : item?.text;
                      const itemPremium = typeof item === 'object' && !!item?.premium;
                      const locked = itemPremium && !isPremiumUnlocked;
                      return (
                        <li
                          key={`${slide.key}-${j}`}
                          className={clsx(styles.item, { [styles.itemLocked]: locked })}
                        >
                          <span className={styles.bullet} aria-hidden>
                            {locked ? (
                              <Lock size={12} strokeWidth={2.6} />
                            ) : (
                              <span className={styles.bulletDot} />
                            )}
                          </span>
                          <span className={styles.itemText}>
                            {text}
                            {locked && (
                              <span
                                className={clsx(
                                  styles.premiumTag,
                                  styles.premiumTagInline,
                                  styles.premiumTagLocked,
                                )}
                              >
                                {t('home.staticBlock.whatsNew.premiumBadge')}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>

                  {showPremiumNote && (
                    <div className={styles.premiumNote}>
                      <Crown size={16} strokeWidth={2.4} aria-hidden />
                      <span>
                        {lockedSlide
                          ? t('home.staticBlock.whatsNew.premiumSlideNote')
                          : t('home.staticBlock.whatsNew.premiumItemsNote')}
                      </span>
                      <button type="button" className={styles.premiumLink} onClick={goToUpgrade}>
                        {t('home.staticBlock.whatsNew.discoverPremium')}
                      </button>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <footer className={styles.footer}>
          <div className={styles.navButtons}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={prev}
              disabled={index === 0}
              aria-label={t('home.staticBlock.whatsNew.back')}
            >
              <ChevronLeft size={18} strokeWidth={2.4} aria-hidden />
              <span className={styles.navLabel}>{t('home.staticBlock.whatsNew.back')}</span>
            </button>
            <button type="button" className={styles.primaryBtn} onClick={next}>
              {isLast ? t('home.staticBlock.whatsNew.gotIt') : t('home.staticBlock.whatsNew.next')}
              {!isLast && <ChevronRight size={18} strokeWidth={2.4} aria-hidden />}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

export default WhatsNewModal;
